import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { MailCheck } from 'lucide-react';
import { t } from '@/i18n';
import { api, errorMessage } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { AuthLayout } from './AuthLayout';
import { PasswordInput } from './LoginPage';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await api('/api/auth/password/forgot', { body: { email } });
      setSent(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title={t('auth.forgotTitle')}
      subtitle={t('auth.forgotSubtitle')}
      footer={
        <Link to="/login" className="font-medium text-accent-fg hover:underline">
          {t('auth.backToLogin')}
        </Link>
      }
    >
      {sent ? (
        <div className="flex items-start gap-3 rounded-panel bg-success-soft p-4 text-sm text-success dark:bg-success/15" role="status">
          <MailCheck className="mt-0.5 size-5 shrink-0" />
          {t('auth.forgotSent')}
        </div>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-4">
          <Input label={t('auth.email')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required autoFocus error={error} />
          <Button type="submit" variant="primary" size="lg" loading={loading}>
            {t('auth.forgotSend')}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await api('/api/auth/password/reset', { body: { token: params.get('token') ?? '', password } });
      navigate('/dashboard', { replace: true });
      location.reload();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title={t('auth.resetTitle')}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <PasswordInput label={t('auth.newPassword')} value={password} onChange={setPassword} autoComplete="new-password" hint={t('auth.passwordHint')} error={error} />
        <Button type="submit" variant="primary" size="lg" loading={loading}>
          {t('auth.resetSave')}
        </Button>
      </form>
    </AuthLayout>
  );
}
